<template>
  <div class="date-range-picker-container">
    <div class="date-range-picker">
      <div class="calendar">
        <div class="month-navigation">
          <button @click="prevMonth" :disabled="isPrevMonthDisabled">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M14.9995 6.70461C14.6095 6.31461 13.9795 6.31461 13.5895 6.70461L8.99953 11.2946C8.60953 11.6846 8.60953 12.3146 8.99953 12.7046L13.5895 17.2946C13.9795 17.6846 14.6095 17.6846 14.9995 17.2946C15.3895 16.9046 15.3895 16.2746 14.9995 15.8846L11.1195 11.9946L14.9995 8.11461C15.3895 7.72461 15.3795 7.08461 14.9995 6.70461Z"
                fill="#333333"
              />
            </svg>
          </button>
          <span>{{ currentMonthName }} {{ currentYear }}</span>
          <button @click="nextMonth" :disabled="isNextMonthDisabled">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M8.99953 6.71075C8.60953 7.10075 8.60953 7.73075 8.99953 8.12075L12.8795 12.0008L8.99953 15.8808C8.60953 16.2708 8.60953 16.9008 8.99953 17.2908C9.38953 17.6808 10.0195 17.6808 10.4095 17.2908L14.9995 12.7008C15.3895 12.3108 15.3895 11.6808 14.9995 11.2908L10.4095 6.70075C10.0295 6.32075 9.38953 6.32075 8.99953 6.71075Z"
                fill="#333333"
              />
            </svg>
          </button>
        </div>
        <div class="weekdays">
          <span
            v-for="(day, index) in weekdays"
            :key="day + currentMonthName + index"
            >{{ day }}</span
          >
        </div>
        <div class="days">
            <div v-for="(day, index) in days"
            :key="day.date + currentMonthName + index" class="day-container">
                <span
                  :class="getDayClass(day)"
                  @click="selectDate(day.date)"
                >
                  {{ day.day }}
                </span>
            </div>
        </div>
      </div>
      <div class="time-selection">
        <div class="time-input">
          <label for="start-time"
            >{{ resource["scheduleStartTime"]
            }}<i
              id="feature-tooltip-start-time"
              class="icon-info cursor-pointer"
            ></i
          ></label>
          <b-tooltip
            target="feature-tooltip-start-time"
            placement="bottom"
            :delay="{ show: 250, hide: 200 }"
            custom-class="feature-tooltip-date-range"
            boundary="viewport"
          >
            {{ resource["scheduleStartTimeTooltip"] }}
          </b-tooltip>
          <multiselect
            ref="multiselectStartTime"
            v-model="startTime"
            placeholder="00:00"
            :options="filteredTimeList"
            :multiple="false"
            :searchable="true"
            :close-on-select="true"
            :hide-selected="false"
            :max-height="200"
            :disabled="isStartDateFixed"
            :taggable="true"
            tagPlaceholder="Add new time"
            @tag="addTag($event, 'startTime')"
            @close="handleClose"
            :searchQuery="startSearchQuery"
            @input="(val) => handleInput(val, 'startTime')"
            @search-change="(query) => handleTimeSearch(query, 'startTime')"
          >
            <template #singleLabel="props">
              <span class="option__desc">
                <span class="option__title">
                  {{ props.option.label ? props.option.label : props.option }}
                </span>
              </span>
            </template>
            <template #option="props">
              <div class="option__desc">
                <span class="option__title">{{ props.option.label ? props.option.label : props.option }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <div class="time-input">
          <label for="end-time"
            >{{ resource["scheduleEndTime"]
            }}<i
              id="feature-tooltip-end-time"
              class="icon-info cursor-pointer"
            ></i
          ></label>
          <b-tooltip
            target="feature-tooltip-end-time"
            placement="bottom"
            :delay="{ show: 250, hide: 200 }"
            custom-class="feature-tooltip-date-range"
            boundary="viewport"
          >
            {{ resource["scheduleEndTimeTooltip"] }}
          </b-tooltip>
          <multiselect
            ref="multiselectEndTime"
            v-model="endTime"
            placeholder="00:00"
            :options="filteredTimeList"
            :multiple="false"
            :searchable="true"
            :close-on-select="true"
            :hide-selected="false"
            :max-height="200"
            :taggable="true"
            tagPlaceholder="Add new time"
            @tag="addTag($event, 'endTime')"
            @close="handleClose"
            :searchQuery="endSearchQuery"
            @input="(val) => handleInput(val, 'endTime')"
            @search-change="(query) => handleTimeSearch(query, 'endTime')"
          >
            <template #singleLabel="props">
              <span class="option__desc">
                <span class="option__title">
                  {{ props.option.label ? props.option.label : props.option }}
                </span>
              </span>
            </template>
            <template #option="props">
              <div class="option__desc">
                <span class="option__title">{{ props.option.label ? props.option.label : props.option }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <span class="time-note">
          {{ resource["scheduleTimeNote"] }}
        </span>
      </div>
    </div>
    <div class="info-box" v-if="!localShowDateModalError">
      <template v-if="selectedDaysCount">
        <i class="icon-info"></i
        ><span
          v-html="
            resource['scheduleSelectedDaysInfo'].replace(
              '{value}',
              selectedDaysCount
            )
          "
        ></span>
      </template>
      <template v-else>
        <i class="icon-info"></i>{{ resource["scheduleNotSelectDaysInfo"] }}
      </template>
    </div>
    <div class="info-box info-box--error" v-if="localShowDateModalError">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M10.0004 4.57767L16.2754 15.4193H3.72543L10.0004 4.57767ZM2.28376 14.586C1.64209 15.6943 2.44209 17.086 3.72543 17.086H16.2754C17.5588 17.086 18.3588 15.6943 17.7171 14.586L11.4421 3.74434C10.8004 2.636 9.20043 2.636 8.55876 3.74434L2.28376 14.586ZM9.16709 8.75267V10.4193C9.16709 10.8777 9.54209 11.2527 10.0004 11.2527C10.4588 11.2527 10.8338 10.8777 10.8338 10.4193V8.75267C10.8338 8.29434 10.4588 7.91934 10.0004 7.91934C9.54209 7.91934 9.16709 8.29434 9.16709 8.75267ZM9.16709 12.9193H10.8338V14.586H9.16709V12.9193Z"
          fill="#631212"
        />
      </svg>
      {{ errorText }}
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  name: 'DateRangePicker',
  components: {
    Multiselect,
  },
  props: {
    value: {
      type: Object,
      default: () => ({ startDate: null, endDate: null }),
    },
    showDateModalError: {
      type: Boolean,
      default: false,
    },
    isStartDateFixed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth(),
      today: new Date(),
      internalStartDate: this.value.startDate,
      internalEndDate: this.value.endDate,
      selectingEndDate: false,
      weekdays: ['s', 'm', 't', 'w', 't', 'f', 's'],
      minDate: new Date(),
      maxDate: (() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        return date;
      })(),
      startTime: null,
      endTime: null,
      timeList: [],
      originalTimeList: [],
      localShowDateModalError: this.showDateModalError,
      errorText: '',
      errorTypes: {
        REQUIRED: '',
        MIN_DAY: '',
        MAX_DAY: '',
        AUTO_ADJUSTED_TIME: '',
        INVALID_TIME_RANGE: '',
      },
      searchQuery: '',
      startSearchQuery: '',
      endSearchQuery: '',
    };
  },
  created() {
    this.generateTimeList();
    const now = new Date();

    this.startTime = this.formatTime(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
      ),
    );

    if (this.isStartDateFixed) {
      this.internalStartDate = now;
    } else {
      if (this.value.startDate) {
        this.internalStartDate = new Date(this.value.startDate);
      }
      if (this.value.startTime) {
        this.startTime = this.value.startTime;
      }
    }

    if (this.value.endDate) {
      this.internalEndDate = new Date(this.value.endDate);
    }
    if (this.value.endTime) {
      this.endTime = this.value.endTime;
    }

    this.errorTypes = {
      REQUIRED: this.resource?.scheduleRequiredError,
      MIN_DAY: this.resource?.scheduleMinDayError,
      MAX_DAY: this.resource?.scheduleMaxDayError,
      AUTO_ADJUSTED_TIME: this.resource?.scheduleMinDayError,
      INVALID_TIME_RANGE: this.resource?.scheduleInvalidTimeRangeError,
    };

    this.$nextTick(() => {
      this.validateStartTime();
    });
  },
  computed: {
    filteredTimeList() {
      if (!this.searchQuery) {
        return this.timeList;
      }
      return this.timeList.filter((time) => time.toLowerCase().includes(this.searchQuery.toLowerCase()));
    },
    currentMonthName() {
      return new Date(this.currentYear, this.currentMonth).toLocaleString(
        'en-US',
        {
          month: 'long',
        },
      );
    },
    days() {
      const days = [];
      const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
      const lastDayOfMonth = new Date(
        this.currentYear,
        this.currentMonth + 1,
        0,
      );
      const startDay = firstDayOfMonth.getDay();
      const totalDays = lastDayOfMonth.getDate();

      // eslint-disable-next-line no-plusplus
      for (let i = startDay - 1; i >= 0; i--) {
        const date = new Date(this.currentYear, this.currentMonth, -i);
        days.push({ day: date.getDate(), date, isOtherMonth: true });
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= totalDays; i++) {
        const date = new Date(this.currentYear, this.currentMonth, i);
        days.push({ day: i, date, isOtherMonth: false });
      }

      const remainingDays = 42 - days.length;
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(this.currentYear, this.currentMonth + 1, i);
        days.push({ day: date.getDate(), date, isOtherMonth: true });
      }

      return days;
    },
    isPrevMonthDisabled() {
      return new Date(this.currentYear, this.currentMonth, 1) <= this.minDate;
    },
    isNextMonthDisabled() {
      return (
        new Date(this.currentYear, this.currentMonth + 1, 0) >= this.maxDate
      );
    },
    selectedDaysCount() {
      if (this.internalStartDate && this.internalEndDate) {
        const start = new Date(this.internalStartDate);
        const end = new Date(this.internalEndDate);

        // Saat farkını hesapla
        const diffInHours = (end - start) / (1000 * 60 * 60);

        // Eğer tam 24 saat veya daha az ise 1 gün
        if (diffInHours <= 24) {
          return 1;
        }

        // 24 saati geçen kısım için gün hesabı
        const days = Math.ceil(diffInHours / 24);
        return days;
      }
      return 0;
    },
  },
  watch: {
    startTime: {
      handler() {
        this.updateStartDateTime();
      },
      immediate: false,
    },
    endTime(newVal) {
      if (this.internalStartDate && this.internalEndDate) {
        const [startHours, startMinutes] = this.startTime
          .split(':')
          .map(Number);
        const [endHours, endMinutes] = newVal.split(':').map(Number);

        const startDateTime = new Date(this.internalStartDate);
        const endDateTime = new Date(this.internalEndDate);

        startDateTime.setHours(startHours, startMinutes, 0, 0);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        // Gün farkını ve saat farkını kontrol et
        const diffInHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
        const diffInDays = diffInHours / 24;

        if (diffInDays > 7) {
          // 7 günden fazla olursa, saati başlangıç saatine eşitle
          endDateTime.setHours(startHours, startMinutes, 0, 0);
          this.endTime = this.startTime;
          this.errorText = this.errorTypes.MAX_DAY;
          this.localShowDateModalError = true;
        }
      }
      this.updateEndDateTime();
    },
    internalStartDate() {
      this.validateStartTime();
      this.emitSelectedDates();
    },
    internalEndDate() {
      this.emitSelectedDates();
    },
    showDateModalError(newVal) {
      this.localShowDateModalError = newVal;
      if (newVal && this.errorText === '') {
        this.errorText = this.errorTypes.REQUIRED;
      }
    },
  },
  methods: {
    validateStartTime() {
      if (!this.internalStartDate || !this.startTime) return;

      const now = new Date();
      if (this.isSameDay(this.internalStartDate, now)) {
        const [hours, minutes] = this.startTime.split(':').map(Number);
        const selectedTime = new Date(this.internalStartDate);
        selectedTime.setHours(hours, minutes, 0, 0);

        if (selectedTime < now) {
          this.startTime = this.formatTime(now);
          this.internalStartDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
          this.errorText = this.errorTypes.AUTO_ADJUSTED_TIME;
          this.localShowDateModalError = true;
          this.emitSelectedDates();
        }
      }
    },
    addTag(newTag, name) {
      const tagValue = typeof newTag === 'object' ? newTag.label : newTag;
      const formattedTime = this.formatTimeInput(tagValue);

      if (this.isValidTimeFormat(formattedTime)) {
        const [rawHours, rawMinutes = ''] = formattedTime.split(':');

        // Process hours
        let hours = rawHours.slice(0, 2).padStart(2, '0');
        hours = Math.min(23, Math.max(0, parseInt(hours, 10) || 0))
          .toString()
          .padStart(2, '0');

        // Process minutes
        let minutes = rawMinutes.slice(0, 2).padStart(2, '0');
        minutes = Math.min(59, Math.max(0, parseInt(minutes, 10) || 0))
          .toString()
          .padStart(2, '0');

        const finalTime = `${hours}:${minutes}`;

        if (!this.originalTimeList.includes(finalTime)) {
          this.timeList.unshift(finalTime);
          this.originalTimeList.unshift(finalTime);
        }

        const now = new Date();
        const [inputHours, inputMinutes] = formattedTime.split(':').map(Number);
        const inputTime = new Date();
        inputTime.setHours(inputHours, inputMinutes, 0, 0);

        if (name === 'startTime') {
          if (this.isSameDay(this.internalStartDate, now) && inputTime < now) {
            this[name] = this.formatTime(now);
            this.errorText = this.errorTypes.AUTO_ADJUSTED_TIME;
            this.localShowDateModalError = true;
          } else {
            this[name] = formattedTime;
          }

          // EndDate seçili ve bugün ise endTime'ı güncelle
          if (this.internalEndDate && this.isSameDay(this.internalEndDate, now)) {
            const minEndTime = new Date(inputTime);
            minEndTime.setMinutes(minEndTime.getMinutes() + 30);

            if (this.endTime) {
              const [endHours, endMinutes] = this.endTime.split(':').map(Number);
              const currentEndTime = new Date();
              currentEndTime.setHours(endHours, endMinutes, 0, 0);

              if (currentEndTime < minEndTime) {
                this.endTime = this.formatTime(minEndTime);
                this.errorText = this.errorTypes.AUTO_ADJUSTED_TIME;
                this.localShowDateModalError = true;
              }
            }
          }
        } else if (name === 'endTime') {
          const startDateTime = new Date(this.internalStartDate);
          const [startHours, startMinutes] = this.startTime.split(':').map(Number);
          startDateTime.setHours(startHours, startMinutes, 0, 0);

          const endDateTime = new Date(this.internalEndDate);
          endDateTime.setHours(inputHours, inputMinutes, 0, 0);

          // Minimum 30 dakika kontrolü
          const minEndTime = new Date(startDateTime);
          minEndTime.setMinutes(minEndTime.getMinutes() + 30);

          // Maksimum 7 gün kontrolü
          const maxEndTime = new Date(startDateTime);
          maxEndTime.setDate(maxEndTime.getDate() + 7);

          if (endDateTime < minEndTime) {
            this[name] = this.formatTime(minEndTime);
            this.errorText = this.errorTypes.AUTO_ADJUSTED_TIME;
            this.localShowDateModalError = true;
          } else if (endDateTime > maxEndTime) {
            this[name] = this.formatTime(maxEndTime);
            this.errorText = this.errorTypes.MAX_DAY;
            this.localShowDateModalError = true;
          } else {
            this[name] = formattedTime;
          }
        }

        this.updateStartDateTime();
        this.updateEndDateTime();
      }

      this.searchQuery = '';
      this.handleClose();

      // Input'u temizle
      if (this.$refs.multiselect) {
        this.$refs.multiselect.search = '';
      }
    },
    formatTimeInput(input) {
      // Eğer input boşsa veya undefined ise
      if (!input) {
        return '';
      }

      // 1. Tüm non-digit karakterleri temizle
      let nums = input.replace(/\D/g, '');

      // 2. İlk 4 karakteri al (HHmm)
      nums = nums.slice(0, 4);

      // 3. Sayıları parse et ve formatlama kurallarını uygula
      let hours = '';
      let mins = '';

      if (nums.length <= 2) {
        // Sadece saat girişi varsa
        hours = nums;
        if (Number(hours) > 23) {
          hours = '23';
        }
      } else {
        // Saat ve dakika girişi varsa
        hours = nums.slice(0, 2);
        mins = nums.slice(2);

        if (Number(hours) > 23) {
          hours = '23';
        }

        if (Number(mins) > 59) {
          if (mins.length === 1) {
            mins = `0${mins[0]}`;
          } else {
            mins = '59';
          }
        }
      }

      // 4. Formatı oluştur
      let formatted = hours;
      if (nums.length > 2) {
        formatted += `:${mins}`;
      } else if (nums.length === 2) {
        formatted += ':';
      }

      return formatted;
    },
    isValidTimeFormat(time) {
      // Check if the time matches HH:mm format and is valid
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      return timeRegex.test(time);
    },
    handleTimeSearch(query, name) {
      const formatted = this.formatTimeInput(query);
      if (name === 'startTime') {
        this.startSearchQuery = formatted;
        this.$refs.multiselectStartTime.search = formatted;
      } else {
        this.endSearchQuery = formatted;
        this.$refs.multiselectEndTime.search = formatted;
      }

      this.timeList = this.originalTimeList.filter((t) => t.includes(formatted));
    },
    handleClose() {
      this.$nextTick(() => {
        this.timeList = [...this.originalTimeList];
        this.searchQuery = '';
      });
    },
    // handleInput(value) {
    //   console.log('value', value);
    //   debugger;
    //   if (!value) {
    //     this.timeList = [...this.originalTimeList];
    //     return;
    //   }
    //   if (typeof value === 'string' && !this.isValidTimeFormat(value)) {
    //     const formattedTime = this.formatTimeInput(value);
    //     if (this.isValidTimeFormat(formattedTime)) {
    //       this.startTime = {
    //         id: formattedTime,
    //         label: formattedTime,
    //       };
    //     }
    //   }
    // },

    handleInput(val, name) {
      if (!val) {
        this.timeList = [...this.originalTimeList];
        return;
      }
      const formatted = this.formatTimeInput(val);
      if (name === 'startTime') {
        this.startSearchQuery = formatted;
        this.startTime = formatted;
      } else {
        this.endSearchQuery = formatted;
        this.endTime = formatted;
      }
    },
    formatTime(date) {
      return date
        .toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // 24 saatlik format
        })
        .replace(/^24:/, '00:'); // Gece yarısı için düzeltme
    },
    generateTimeList() {
      const times = [];
      // eslint-disable-next-line no-plusplus
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const formattedHour = hour.toString().padStart(2, '0');
          const formattedMinute = minute.toString().padStart(2, '0');
          times.push(`${formattedHour}:${formattedMinute}`);
        }
      }
      this.timeList = times;
      this.originalTimeList = times;
    },
    updateStartDateTime() {
      if (this.internalStartDate && this.startTime) {
        const [hours, minutes] = this.startTime.split(':').map(Number);
        this.internalStartDate.setHours(hours, minutes, 0, 0);
        this.validateStartTime();
        this.emitSelectedDates();
      }
    },
    updateEndDateTime() {
      if (this.internalEndDate && this.endTime) {
        const [hours, minutes] = this.endTime.split(':').map(Number);
        const startDateTime = new Date(this.internalStartDate);
        const endDateTime = new Date(this.internalEndDate);

        endDateTime.setHours(hours, minutes, 0, 0);

        // Minimum 1 gün ve maksimum 7 gün kontrolü
        const diffInHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
        const diffInDays = diffInHours / 24;

        if (diffInHours < 0.5) {
          endDateTime.setDate(startDateTime.getDate());
          endDateTime.setHours(
            startDateTime.getHours(),
            startDateTime.getMinutes() + 30,
            0,
            0,
          );
          this.endTime = this.formatTime(endDateTime);
          this.errorText = this.errorTypes.AUTO_ADJUSTED_TIME;
          this.localShowDateModalError = true;
        } else if (diffInDays > 7) {
          // 7 günden fazla olursa, 7. güne ayarla
          const newEndDate = this.addDays(startDateTime, 7);
          newEndDate.setHours(
            startDateTime.getHours(),
            startDateTime.getMinutes(),
            0,
            0,
          );
          this.internalEndDate = newEndDate;
          this.endTime = this.startTime;
          this.errorText = this.errorTypes.MAX_DAY;
          this.localShowDateModalError = true;
        }

        this.internalEndDate = endDateTime;
        this.emitSelectedDates();
      }
    },
    emitSelectedDates() {
      const formattedStartDate = this.internalStartDate
        ? this.internalStartDate.toISOString()
        : null;
      const formattedEndDate = this.internalEndDate
        ? this.internalEndDate.toISOString()
        : null;
      if (this.localShowDateModalError) {
        this.errorText = '';
        this.localShowDateModalError = false;
      }

      this.$emit('input', {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        daysCount: this.selectedDaysCount,
        startTime: this.startTime,
        endTime: this.endTime,
      });
    },
    prevMonth() {
      if (!this.isPrevMonthDisabled) {
        if (this.currentMonth === 0) {
          this.currentMonth = 11;
          // eslint-disable-next-line no-plusplus
          this.currentYear--;
        } else {
          // eslint-disable-next-line no-plusplus
          this.currentMonth--;
        }
      }
    },
    nextMonth() {
      if (!this.isNextMonthDisabled) {
        if (this.currentMonth === 11) {
          this.currentMonth = 0;
          // eslint-disable-next-line no-plusplus
          this.currentYear++;
        } else {
          // eslint-disable-next-line no-plusplus
          this.currentMonth++;
        }
      }
    },
    selectDate(date) {
      if (!date) return;

      // Tarihleri normalize et
      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);

      const minNormalizedDate = new Date(this.minDate);
      minNormalizedDate.setHours(0, 0, 0, 0);

      const maxNormalizedDate = new Date(this.maxDate);
      maxNormalizedDate.setHours(0, 0, 0, 0);

      // Tarih validasyonları
      if (
        normalizedDate > maxNormalizedDate
        || normalizedDate < minNormalizedDate
      ) return;

      const selectedDate = new Date(normalizedDate);

      if (this.isStartDateFixed) {
        if (this.isSameDay(selectedDate, this.internalStartDate)) {
          // Aynı gün seçildiğinde
          const endDateTime = new Date(selectedDate);
          const startDateTime = new Date(this.internalStartDate);
          // End time'ı start time'dan 30 dakika sonraya ayarla
          endDateTime.setHours(
            startDateTime.getHours(),
            startDateTime.getMinutes() + 30,
            0,
            0,
          );
          this.endTime = this.formatTime(endDateTime);
          this.internalEndDate = endDateTime;
          this.selectingEndDate = false;
          this.updateEndDateTime();
        }
        // Seçilen tarih başlangıç tarihinden küçükse işlem yapma
        if (selectedDate < this.internalStartDate) {
          this.errorText = this.errorTypes.MIN_DAY;
          this.localShowDateModalError = true;
          return;
        }

        // End date'i direkt set et
        let endDateTime = new Date(selectedDate);
        const startDateTime = new Date(this.internalStartDate);

        const [startHours, startMinutes] = this.startTime
          .split(':')
          .map(Number);
        endDateTime.setHours(startHours, startMinutes, 0, 0);

        const diffInHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
        const diffInDays = diffInHours / 24;

        if (diffInDays > 7) {
          // Maksimum 7 gün için ayarlama
          endDateTime = this.addDays(startDateTime, 7);
          this.errorText = this.errorTypes.MAX_DAY;
          this.localShowDateModalError = true;
        }

        // Saatleri ayarla
        endDateTime.setHours(
          startDateTime.getHours(),
          startDateTime.getMinutes(),
          0,
          0,
        );
        this.endTime = this.formatTime(endDateTime);

        this.internalEndDate = endDateTime;
        this.updateEndDateTime();
      } else {
        // Normal süreç
        // eslint-disable-next-line no-lonely-if
        if (
          !this.internalStartDate
          || (this.internalStartDate && this.internalEndDate)
        ) {
          this.internalStartDate = selectedDate;
          this.updateStartDateTime();
          this.internalEndDate = null;
          this.selectingEndDate = true;
        } else if (this.selectingEndDate) {
          if (this.isSameDay(selectedDate, this.internalStartDate)) {
            // Aynı gün seçildiğinde
            const endDateTime = new Date(selectedDate);
            const startDateTime = new Date(this.internalStartDate);
            // End time'ı start time'dan 30 dakika sonraya ayarla
            endDateTime.setHours(
              startDateTime.getHours(),
              startDateTime.getMinutes() + 30,
              0,
              0,
            );
            this.endTime = this.formatTime(endDateTime);
            this.internalEndDate = endDateTime;
            this.selectingEndDate = false;
            this.updateEndDateTime();
          }
          if (selectedDate < this.internalStartDate) {
            this.internalStartDate = selectedDate;
            this.updateStartDateTime();
          } else {
            const startDateTime = new Date(this.internalStartDate);
            let endDateTime = new Date(selectedDate);

            const [startHours, startMinutes] = this.startTime
              .split(':')
              .map(Number);

            endDateTime.setHours(startHours, startMinutes + 30, 0, 0);

            const diffInHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
            const diffInDays = diffInHours / 24;

            if (diffInDays < 1) {
              // Minimum 1 gün için ayarlama
              endDateTime = startDateTime;
              this.errorText = this.errorTypes.MIN_DAY;
              this.localShowDateModalError = true;
            } else if (diffInDays > 7) {
              // Maksimum 7 gün için ayarlama
              endDateTime = this.addDays(startDateTime, 7);
              this.errorText = this.errorTypes.MAX_DAY;
              this.localShowDateModalError = true;
            }

            // Saatleri ayarla
            endDateTime.setHours(
              startDateTime.getHours(),
              startDateTime.getMinutes(),
              0,
              0,
            );
            this.endTime = this.formatTime(endDateTime);

            this.internalEndDate = endDateTime;
            this.selectingEndDate = false;
            this.updateEndDateTime();
          }
        }
      }

      this.emitSelectedDates();
    },
    updateValue() {
      this.$emit('input', {
        startDate: this.internalStartDate,
        endDate: this.internalEndDate,
      });
    },
    getDayClass(day) {
      if (!day.date) return 'empty';

      const classList = [];
      const isToday = day.date.getFullYear() === this.today.getFullYear()
        && day.date.getMonth() === this.today.getMonth()
        && day.date.getDate() === this.today.getDate();

      if (day.isOtherMonth) classList.push('other-month');

      // Başlangıç tarihi seçilmişse, 7 günden sonrasını disable et
      if (this.internalStartDate && !this.internalEndDate) {
        const maxDate = new Date(this.internalStartDate);
        maxDate.setDate(maxDate.getDate() + 7);
        if (day.date > maxDate) {
          classList.push('disabled');
        }
      }

      // Normal disable kontrolü
      if (!isToday && (day.date < this.minDate || day.date > this.maxDate)) {
        classList.push('disabled');
      }

      if (isToday) {
        classList.push('today');
      }

      // Start date seçiliyse ve bugünden önceki tarihleri disable et
      if (this.isStartDateFixed && day.date < this.internalStartDate && !isToday) {
        classList.push('no-pointer-events');
      }

      if (
        this.internalStartDate
        && day.date.getFullYear() === this.internalStartDate.getFullYear()
        && day.date.getMonth() === this.internalStartDate.getMonth()
        && day.date.getDate() === this.internalStartDate.getDate()
      ) {
        classList.push('start-date');
      }

      if (
        this.internalEndDate
        && day.date.getFullYear() === this.internalEndDate.getFullYear()
        && day.date.getMonth() === this.internalEndDate.getMonth()
        && day.date.getDate() === this.internalEndDate.getDate()
      ) {
        classList.push('end-date');
      }

      if (
        this.internalStartDate
        && this.internalEndDate
        && day.date > this.internalStartDate
        && day.date < this.internalEndDate
      ) {
        classList.push('in-range');
      }

      return classList.length ? `day ${classList.join(' ')}` : 'day';
    },
    addDays(date, days) {
      const result = new Date(date);

      // Gün gün ekleyerek ay sınırlarını kontrol et
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < days; i++) {
        result.setDate(result.getDate() + 1);

        // Eğer ay değiştiyse ve gün 1 değilse, ayın son gününe ayarla
        if (result.getDate() === 1 && i < days - 1) {
          result.setDate(0); // Bir önceki ayın son günü
          result.setDate(result.getDate() + 1);
        }
      }

      return result;
    },
    isSameDay(date1, date2) {
      if (!date1 || !date2) return false;
      return (
        date1.getFullYear() === date2.getFullYear()
        && date1.getMonth() === date2.getMonth()
        && date1.getDate() === date2.getDate()
      );
    },
  },
};
</script>

<style scoped lang="scss">
.date-range-picker-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.date-range-picker {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 32px;
}

.calendar {
  width: 210px;
}

.month-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  span {
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Subtitle/3 - Medium */
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }

  button {
    padding: 0px !important;
    height: auto !important;
    border-radius: 0 !important;
    outline: 0 !important;
    border: none;

    &:disabled {
      background: transparent !important;
      opacity: 0.3 !important;
      cursor: not-allowed;
    }
  }
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-weight: bold;
  margin-bottom: 5px;

  span {
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    font-family: Roboto;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    opacity: 0.7;
  }
}

.days {
  display: grid;
  grid-template-columns: repeat(7, 30px);
  text-align: center;
}

.day-container {
    display: flex;
    padding: 4px;
    align-items: flex-start;
    gap: 10px;
    height: 30px;

    &:has(.start-date) {
        padding-right: 0;
    }
    &:has(.start-date):has(.today):has(.no-pointer-events) {
        padding-right: 4px;
    }
    &:has(.in-range) {
        padding-right: 0;
        padding-left: 0;
    }
    &:has(.end-date) {
        padding-left: 0;
    }

    &:has(.today):has(.start-date):has(.no-pointer-events) {
        .today {
            border-radius: 100%;
        }
        padding-left: 4px !important;
    }
}

.days {
    &:has(.today):has(.start-date):has(.no-pointer-events):has(.in-range) {
        .today {
            border-radius: 99px 0px 0px 99px;
        }
        .day-container {
            padding-right: 0 !important;
        }
    }
}

.day,
.other-month,
.empty {
  cursor: pointer;
  color: var(--Text-Colors-Primary-Gray-Text, #333);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  display: flex;
  justify-content: center;

  width: 30px;
  padding: 5px;
  align-items: flex-start;
  gap: 10px;
}

.other-month {
  color: #878787 !important;
  opacity: 0.7;
}

.today {
  border-radius: 99px;
  border: 1px solid #ffe9ce;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 22px;
}

.disabled {
  color: #878787;
  cursor: not-allowed;
  pointer-events: all;
  opacity: 0.3;
}

.start-date {
  background-color: #ffe9ce;
  border-radius: 99px 0px 0px 99px;
}

.end-date {
  background-color: #ffe9ce;
  border-radius: 0px 99px 99px 0px;
}

.start-date.end-date {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.start-end-date {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.single-day {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.in-range {
  background-color: #ffe9ce;
}

.selected-range {
  margin-top: 15px;
  font-weight: bold;
}

.time-selection {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 258px;
}

.time-input {
  display: flex;
  flex-direction: column;

  label {
    display: flex;
    align-items: center;
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Subtitle/3 - Medium */
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 4px !important;
  }

  i {
    font-size: 16px;
    margin-left: 4px;
  }
}

.time-note {
  color: var(--Text-Colors-Secondary-Gray-Text, #878787);
  font-feature-settings: "liga" off, "clig" off;
  /* Typography/Small Text/1 - Regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.info-box {
  width: 500px;
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  margin: 24px auto 0;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid var(--Stroke-Colors-Primary-Gray-Stroke, #e5e5e5);
  background: var(--Background-Colors-Bright-Gray, #fafafa);
  color: var(--Text-Colors-Primary-Gray-Text, #333);
  font-feature-settings: "liga" off, "clig" off;
  /* Typography/Small Text/1 - Regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 150% */

  i {
    font-size: 20px;
  }

  &--error {
    border: 1px solid var(--Stroke-Colors-Light-Red-Stroke, #f2b5b5);
    background: var(--Background-Colors-Light-Red, #f8dada);
    color: #631212;
  }
}

.quick-select-buttons {
  display: flex;
  gap: 10px;
  margin-top: 10px;

  button {
    padding: 5px 10px;
    background-color: #ffe9ce;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #ffd580;
    }
  }
}

.multiselect {
  width: 258px;
  height: 36px;
  min-height: 36px;
}

::v-deep {
  .multiselect__select {
    height: 40px !important;
    min-height: 38px !important;
    padding: 8px !important;
    top: 0;
    right: 0;
    border-radius: 4px;
    border: 1px solid var(--Stroke-Colors-Tertiary-Gray-Stroke, #c3c3c3);
  }

  .multiselect__tags {
    height: 40px !important;
    min-height: 38px !important;
    padding: 8px !important;
  }

  .multiselect__single {
    margin-bottom: 0;
  }

  .multiselect__placeholder,
  .multiselect__single {
    margin-bottom: 0;
    display: flex;
    width: 100%;
    align-items: center;
    height: 100%;
  }

  .multiselect__option--selected {
    background: #eee;
  }

  .option__title {
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: #333;
  }

  .multiselect__content-wrapper {
    z-index: 999;
    top: 42px;
    border-radius: 4px !important;
    border-color: #e5e5e5;
    box-shadow: 0px 2px 12px 1px rgba(0, 0, 0, 0.12);
  }

  .multiselect__option {
    padding: 6px 16px;
    height: 36px;
    display: flex;
    align-items: center;
  }

  .multiselect__option--highlight {
    background: #eee;
  }

  .multiselect__option--selected:after {
    display: none;
  }

  .multiselect__select:before {
    display: none;
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M13.2292 7.49961L9.99583 10.7329L6.7625 7.49961C6.4375 7.17461 5.9125 7.17461 5.5875 7.49961C5.2625 7.82461 5.2625 8.34961 5.5875 8.67461L9.4125 12.4996C9.7375 12.8246 10.2625 12.8246 10.5875 12.4996L14.4125 8.67461C14.7375 8.34961 14.7375 7.82461 14.4125 7.49961C14.0875 7.18294 13.5542 7.17461 13.2292 7.49961Z" fill="%23333333"/></svg>') !important;
    border: none !important;
    top: 0px !important;
  }

  .multiselect__input {
    margin-bottom: 0px !important;
  }

  .multiselect--active {
    .multiselect__select {
      box-shadow: 0px 0px 0px 4px rgba(255, 141, 6, 0.30) !important;
      outline: 1px solid #FF8D06 !important;
    }
  }
}

.feature-tooltip-date-range {
  opacity: 1 !important;

  &.bs-tooltip-right {
    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>');
        width: 17px;
        height: 9px;
      }
    }
  }

  &.bs-tooltip-left {
    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>');
        width: 17px;
        height: 9px;
        transform: rotate(180deg);
      }
    }
  }

  &.bs-tooltip-bottom {
    padding: 0 !important;
    top: 10px !important;

    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>') !important;
        width: 17px;
        height: 9px;
        bottom: 5px;
      }
    }
  }

  ::v-deep .tooltip-inner {
    border: 1px solid var(--Stroke-Colors-Light-Blue-Stroke, #b7e1f3) !important;
    background: var(--Background-Colors-Light-Blue, #dbf0f9) !important;
    border-radius: 4px !important;
    padding: 8px 12px !important;
    width: fit-content !important;
    max-width: fit-content !important;
    box-shadow: 0px 2px 6px 0px rgba(176, 186, 197, 0.6) !important;

    text-align: left;
    color: var(--Text-Colors-Blue-Text, #114e67) !important;
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Small Text/1 - Regular */
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 150% */
  }
}

.no-pointer-events {
  pointer-events: none;
}
</style>
